import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  Paper,
  Grid,
  IconButton,
  AppBar,
  Toolbar,
  Button,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Slide,
  useScrollTrigger,
  Dialog,
  DialogTitle,
  DialogContent,
  Menu,
  MenuItem,
  Divider
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import FolderIcon from "@mui/icons-material/Folder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import UserLogin from "../UserLogin";

// HideOnScroll helper
function HideOnScroll(props) {
  const { children } = props;
  const trigger = useScrollTrigger();
  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const NAV_LINKS = [
  { label: "Home", path: "/user-home" },
  { label: "Albums", path: "/user-albums" },
  { label: "Frames", path: "/user-frames" },
  { label: "Photo Selection", path: "/user-photo-selection" },
  { label: "Prices", path: "/user-prices" }, // <-- Make sure this is present!
  { label: "Contact", path: "/user-contact" },
  { label: "About", path: "/user-about" }
];

function PhotoSelection() {
  const clientMongoId = localStorage.getItem("clientMongoId");
  const userId = localStorage.getItem("userId");
  const [user, setUser] = useState(null);
  const [folders, setFolders] = useState([]);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [folderImages, setFolderImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [likedImages, setLikedImages] = useState([]);
  const [previewImg, setPreviewImg] = useState(null);
  const isMobile = useMediaQuery("(max-width:600px)");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [footers, setFooters] = useState({});
  const studioName = localStorage.getItem("studioName") || "Studio";
  const [isLoggedIn, setIsLoggedIn] = useState(!!userId);

  useEffect(() => {
    async function fetchUser() {
      try {
        const res = await axios.get(
          `https://studio-lc.onrender.com/api/clients/${clientMongoId}/users/${userId}`
        );
        setUser(res.data);
        const images = Array.isArray(res.data?.images) ? res.data.images : [];
        let dbFolders = [...new Set(images.map(img => img.folder).filter(Boolean))];
        const eventCopy = `${res.data.event} Copy`;
        dbFolders = dbFolders.filter(f => f !== "Original Photos" && f !== eventCopy && f !== "framephoto");
        setFolders(["Original Photos", eventCopy, "framephoto", ...dbFolders]);
      } catch (err) {
        setUser(null);
        setFolders([]);
      }
    }
    fetchUser();
  }, [userId, clientMongoId]);

  useEffect(() => {
    async function fetchFooters() {
      if (!clientMongoId) return;
      try {
        const res = await axios.get(
          `https://studio-lc.onrender.com/api/clients/${clientMongoId}/footers`
        );
        setFooters(res.data.footers || {});
      } catch (err) {
        setFooters({});
      }
    }
    fetchFooters();
  }, [clientMongoId]);

  // Show login if not logged in
  if (!isLoggedIn) {
    return (
      <UserLogin
        onBack={() => window.history.back()}
        onLoginSuccess={() => setIsLoggedIn(true)}
      />
    );
  }

  if (!user) return <Typography>Loading...</Typography>;

  return (
    <Box sx={{ bgcolor: "#fff8e1", minHeight: "100vh" }}>
      {/* AppBar at the top */}
      <HideOnScroll>
        <AppBar
          position="fixed"
          sx={{
            bgcolor: "#fff",
            boxShadow: 3,
            height: isMobile ? 56 : 80,
            zIndex: 1201
          }}
        >
          <Toolbar
            sx={{
              minHeight: isMobile ? 56 : 80,
              px: isMobile ? 1 : 3,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: isMobile ? "space-between" : "center"
            }}
          >
            <Typography
              variant={isMobile ? "h5" : "h4"}
              sx={{
                 fontFamily: "Josefin Sans",
                fontWeight: 900,
                color: "#8b5e3c",
                letterSpacing: 2,
                mr: isMobile ? 0 : 4,
                flexShrink: 0
              }}
            >
              {studioName}
            </Typography>
            {!isMobile && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 4,
                  flexGrow: 1
                }}
              >
                {NAV_LINKS.map((link) => (
                  <Button
                    key={link.label}
                    href={link.path}
                    sx={{
                      color: "#8b5e3c",
                       fontFamily: "Josefin Sans",
                      fontWeight: 700,
                      fontSize: 22,
                      textTransform: "none",
                      letterSpacing: 1.5
                    }}
                  >
                    {link.label}
                  </Button>
                ))}
              </Box>
            )}
            {isMobile && (
              <IconButton
                edge="end"
                color="inherit"
                aria-label="menu"
                onClick={() => setDrawerOpen(true)}
                sx={{ ml: "auto" }}
              >
                <MenuIcon sx={{ color: "#8b5e3c", fontSize: 36 }} />
              </IconButton>
            )}
          </Toolbar>
        </AppBar>
      </HideOnScroll>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box sx={{ width: 220, bgcolor: "#fff", height: "100%",mt: 6 }}>
          <List>
            {NAV_LINKS.map((link) => (
              <ListItem button key={link.label} onClick={() => setDrawerOpen(false)} component="a" href={link.path}>
                <ListItemText
                  primary={link.label}
                  primaryTypographyProps={{
                    fontWeight: 700,
                    color: "#8b5e3c",
                     fontFamily: "Josefin Sans",
                    fontSize: 18
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
      <Box sx={{ pt: isMobile ? 8 : 10, px: 2, bgcolor: "#f9f9f9", minHeight: "100vh" }}>
        {/* User Details and Folders Section */}
        <Paper
          sx={{
            maxWidth: 1400,
            mx: "auto",
            mt: 5,
            mb: 4,
            borderRadius: 5,
            boxShadow: 6,
            p: 4,
            background: "linear-gradient(135deg, #fffde7 0%, #e3f2fd 100%)"
          }}
        >
          <Grid container spacing={4} alignItems="flex-start">
            {/* User Details Table - Left */}
            <Grid item xs={12} md={5}>
              <Box sx={{ mb: 3, ml: { xs: 0, md: 5 } }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    color: "#8b5e3c",
                     fontFamily: "Josefin Sans",
                    letterSpacing: 1.5,
                    mb: 1,
                    width: { xs: "100%", md: 500 },
                    wordBreak: "break-word"
                  }}
                >
                  {user.name}
                </Typography>
                <Typography sx={{ color: "#888", fontSize: 18, mb: 2, fontFamily: "Jost", }}>
                  {user.event}
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <TableContainer component={Paper} sx={{ boxShadow: 2, borderRadius: 3, bgcolor: "#fff" }}>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: "#8b5e3c", fontFamily: "Josefin Sans", }}>User ID</TableCell>
                      <TableCell sx={{fontFamily: "Josefin Sans",}}>{user.userId}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: "#8b5e3c",fontFamily: "Josefin Sans",  }}>Username</TableCell>
                      <TableCell sx={{fontFamily: "Josefin Sans", }}>{user.username}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: "#8b5e3c",fontFamily: "Josefin Sans", }}>Mobile</TableCell>
                      <TableCell sx={{fontFamily: "Josefin Sans", }}>{user.mobile}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: "#8b5e3c",fontFamily: "Josefin Sans", }}>Place</TableCell>
                      <TableCell sx={{fontFamily: "Josefin Sans", }}>{user.place}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: "#8b5e3c",fontFamily: "Josefin Sans", }}>Event</TableCell>
                      <TableCell sx={{fontFamily: "Josefin Sans", }}>{user.event}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: "#8b5e3c",fontFamily: "Josefin Sans", }}>Status</TableCell>
                      <TableCell sx={{fontFamily: "Josefin Sans", }}>{user.status}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: "#8b5e3c",fontFamily: "Josefin Sans", }}>Date</TableCell>
                      <TableCell sx={{fontFamily: "Josefin Sans", }}>{user.date ? user.date.slice(0, 10) : ""}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            {/* Folders - Right */}
            <Grid item xs={12} md={7}>
              <Paper
                sx={{
                  p: { xs: 2, sm: 3, md: 4 },
                  ml: { xs: 0, md: 7 },
                  mt: 3,
                  borderRadius: 4,
                  boxShadow: 2,
                  background: "linear-gradient(135deg, #e3f2fd 0%, #e8f5e9 100%)",
                  minHeight: 420, // Ensures some height even if folders are few
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  alignItems: "stretch",
                  width:{ xs: "90%", md: "95%"}
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 700, color: "#8b5e3c", mb: 3,fontFamily: "Josefin Sans", }}>
                  Your Folders
                </Typography>
                <Grid container spacing={3}>
                  {folders.length > 0 ? (
                    folders.map((folder, idx) => (
                      <Grid item xs={12} sm={6} md={4} key={folder}>
                        <Paper
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            p: 3,
                            borderRadius: 3,
                            boxShadow: 3,
                            bgcolor: idx === 0 ? "#e3f2fd" : idx === 1 ? "#e8f5e9" : "#fff",
                            cursor: "pointer",
                            transition: "transform 0.2s, box-shadow 0.2s",
                            "&:hover": {
                              transform: "scale(1.04)",
                              boxShadow: 6,
                              bgcolor: "#fffde7"
                            }
                          }}
                          onClick={async () => {
                            if (
                              folder === "Original Photos" ||
                              folder === `${user.event} Copy` ||
                              folder === "framephoto"
                            ) {
                              try {
                                const res = await axios.get(
                                  `https://studio-lc.onrender.com/api/clients/${clientMongoId}/users/${userId}/images?folder=${encodeURIComponent(folder)}`
                                );
                                setFolderImages(Array.isArray(res.data) ? res.data : []);
                                setImageDialogOpen(true);
                              } catch (err) {
                                setFolderImages([]);
                                setImageDialogOpen(false);
                                alert("Failed to load images.");
                              }
                            }
                          }}
                        >
                          <FolderIcon sx={{ color: idx === 0 ? "#1976d2" : idx === 1 ? "#388e3c" : "#ffa726", fontSize: 48, mb: 1 }} />
                          <Typography sx={{ fontWeight: 700, fontSize: 20,fontFamily: "Josefin Sans", }}>
                            {folder}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))
                  ) : (
                    <Grid item xs={12}>
                      <Box sx={{ textAlign: "center", mt: 6 }}>
                        <img
                          src="https://cdn-icons-png.flaticon.com/512/2922/2922510.png"
                          alt="No folders"
                          style={{ width: 120, opacity: 0.3, marginBottom: 16 }}
                        />
                        <Typography variant="h6" sx={{ color: "#888",fontFamily: "Josefin Sans", }}>
                          No folders yet. Start by adding or selecting a folder!
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
                {/* Fill space with a tip or illustration if few folders */}
                {folders.length > 0 && folders.length < 4 && (
                  <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center", mt: 4 }}>
                    <img
                      src="https://cdn-icons-png.flaticon.com/512/1055/1055687.png"
                      alt="Decorative"
                      style={{ width: 100, opacity: 0.15, marginBottom: 8 }}
                    />
                    <Typography variant="body2" sx={{ color: "#aaa",fontFamily: "Josefin Sans", }}>
                      Tip: Click a folder to view and select your photos!
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Paper>

        {/* Image Dialog */}
        <Dialog open={imageDialogOpen} onClose={() => setImageDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ color: "#8b5e3c", fontWeight: 700, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            {folderImages[0]?.folder || "Photos"}
            <Typography component="span" sx={{ fontSize: 18, color: "#a67c52", fontWeight: 700, ml: -55,fontFamily: "Josefin Sans", }}>
              ({folderImages.filter(img => img.src).length} images
              {folderImages[0]?.folder === "Original Photos" && selectedImages.length > 0
                ? `, ${selectedImages.length} selected`
                : ""}
              )
            </Typography>
            <IconButton onClick={e => setMenuAnchorEl(e.currentTarget)}>
              <MoreVertIcon sx={{ color: "#a67c52" }} />
            </IconButton>
            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={() => setMenuAnchorEl(null)}
            >
              {folders
                .filter(f => f !== "Original Photos")
                .map(folderName => (
                  <MenuItem
                    key={folderName}
                    onClick={async () => {
                      setMenuAnchorEl(null);
                      if (selectedImages.length === 0) {
                        alert("Select images to copy.");
                        return;
                      }
                      try {
                        await axios.post(
                          `https://studio-lc.onrender.com/api/clients/${clientMongoId}/users/${userId}/copy-images`,
                          {
                            srcs: selectedImages, // array of Cloudinary URLs
                            toFolder: folderName  // event + " Copy"
                          }
                        );
                        alert(`Images copied to ${folderName}!`);
                      } catch (err) {
                        alert("Failed to copy images.");
                      }
                    }}
                  >
                    Copy to {folderName}
                  </MenuItem>
                ))}
            </Menu>
          </DialogTitle>
          <DialogContent sx={{ display: "flex", gap: 3, minHeight: 400 }}>
            {/* Left: Images grid */}
            <Box sx={{ flex: 1, overflowY: "auto", maxHeight: "60vh" }}>
              <Grid container spacing={2}>
                {folderImages.filter(img => img.src).length === 0 && (
                  <Typography sx={{ color: "#a67c52", m: 2,fontFamily: "Josefin Sans", }}>No images found.</Typography>
                )}
                {folderImages.filter(img => img.src).map((img, idx) => (
                  <Grid item xs={12} sm={6} md={2.4} key={img._id || idx}>
                    <Paper sx={{ p: 1, borderRadius: 2, boxShadow: 2, textAlign: "center" }}>
                      <img
                        src={img.src}
                        alt={img.folder}
                        style={{
                          width: 120,
                          height: 120,
                          objectFit: "cover",
                          borderRadius: 8,
                          marginBottom: 8,
                          cursor: "pointer",
                          border: "2px solid #f5e1c1"
                        }}
                        onClick={() => setPreviewImg(img.src)}
                      />
                      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2 }}>
                        {/* Heart Icon */}
                        <IconButton
                          onClick={() => {
                            setLikedImages(prev =>
                              prev.includes(img.src)
                                ? prev.filter(src => src !== img.src)
                                : [...prev, img.src]
                            );
                          }}
                          sx={{ color: likedImages.includes(img.src) ? "#e53935" : "#a67c52" }}
                        >
                          {likedImages.includes(img.src) ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                        </IconButton>
                        {/* Checkbox Icon */}
                        <IconButton
                          onClick={() => {
                            setSelectedImages(prev =>
                              prev.includes(img.src)
                                ? prev.filter(src => src !== img.src)
                                : [...prev, img.src]
                            );
                          }}
                          sx={{ color: selectedImages.includes(img.src) ? "#1976d2" : "#a67c52" }}
                        >
                          {selectedImages.includes(img.src) ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
                        </IconButton>
                        {/* Delete Icon for images in framephoto folder */}
                        {folderImages[0]?.folder === "framephoto" && (
                          <IconButton
                            onClick={async () => {
                              if (window.confirm("Delete this image?")) {
                                try {
                                  await axios.delete(
                                    `https://studio-lc.onrender.com/api/clients/${clientMongoId}/users/${userId}/images`,
                                    { data: { src: img.src, folder: "framephoto" } }
                                  );
                                  setFolderImages(prev => prev.filter(i => i.src !== img.src));
                                } catch (err) {
                                  alert("Failed to delete image.");
                                }
                              }
                            }}
                            sx={{ color: "#e53935" }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
            {/* Right: Optionally, you can add more controls here */}
          </DialogContent>
        </Dialog>
        {/* Preview Dialog */}
        <Dialog open={!!previewImg} onClose={() => setPreviewImg(null)} maxWidth="md" fullWidth>
          <DialogContent sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 2,
            minHeight: "70vh"
          }}>
            <img
              src={previewImg}
              alt="Preview"
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "70vh",
                borderRadius: 12,
                boxShadow: "0 8px 32px rgba(166,124,82,0.13)",
                objectFit: "contain"
              }}
            />
          </DialogContent>
        </Dialog>
      </Box>
     {/* Footer Section */}
           <Box
                            sx={{
                              bgcolor: "#a67c52",
                              color: "#fff",
                               fontFamily: "Josefin Sans",
                              mt: 4,
                              pt: 4,
                              pb: 2
                            }}
                          >
                            <Grid container spacing={2} sx={{ maxWidth: 1300, mx: "auto" }}>
                              {/* Left Column */}
                              <Grid item xs={12} md={3} sx={{ml: { xs: 3 }, mt: { xs: 6,  }}}>
                                <Typography variant="h5" sx={{ fontWeight: 700,  fontFamily: "Josefin Sans",  }}>
                                  {footers.studio}
                                </Typography>
                                <Typography variant="body1" sx={{  fontFamily: "Jost",fontSize:17}}>{footers.tag}</Typography>
                                <Typography variant="body2" sx={{  fontFamily: "Jost",fontSize:17}}>{footers.handle}</Typography>
                              </Grid>
                              {/* Center Column */}
                              <Grid
                                item
                                xs={12}
                                md={6}
                                sx={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  ml: { xs: 0, md: 12 }
                                }}
                              >
                                <Typography
                                  variant="body1"
                                  sx={{
                                    textAlign: "center",
                                    fontSize: 18,
                                    mb: 2,
                                    fontFamily: "Jost",
                                  }}
                                >
                                  {footers.text}
                                </Typography>
                              </Grid>
                              {/* Right Column */}
                              <Grid item xs={12} md={3}>
                                <Box sx={{ display: "flex", gap: 2, mb: 2,ml: { xs: 3, md: 15 } ,mt: { xs: 6, md: 5 } }}>
                                  {/* WhatsApp */}
                                  <a href="https://wa.me/919123456789" target="_blank" rel="noopener noreferrer">
                                    <Box sx={{ bgcolor: "#25D366", borderRadius: "50%", p: 1 }}>
                                      <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg" alt="WhatsApp" style={{ width: 28, height: 28 }} />
                                    </Box>
                                  </a>
                                  {/* Instagram */}
                                  <a href="https://instagram.com/" target="_blank" rel="noopener noreferrer">
                                    <Box sx={{ bgcolor: "#fff", borderRadius: "50%", p: 1 }}>
                                      <img src="https://upload.wikimedia.org/wikipedia/commons/a/a5/Instagram_icon.png" alt="Instagram" style={{ width: 28, height: 28 }} />
                                    </Box>
                                  </a>
                                  {/* Gmail */}
                                  <a href="mailto:studioartmdu@gmail.com" target="_blank" rel="noopener noreferrer">
                                    <Box sx={{ bgcolor: "#fff", borderRadius: "50%", p: 1 }}>
                                      <img src="https://upload.wikimedia.org/wikipedia/commons/4/4e/Gmail_Icon.png" alt="Gmail" style={{ width: 28, height: 28 }} />
                                    </Box>
                                  </a>
                                  {/* YouTube */}
                                  <a href="https://youtube.com/" target="_blank" rel="noopener noreferrer">
                                    <Box sx={{ bgcolor: "#fff", borderRadius: "50%", p: 1 }}>
                                      <img src="https://upload.wikimedia.org/wikipedia/commons/4/42/YouTube_icon_%282013-2017%29.png" alt="YouTube" style={{ width: 28, height: 28 }} />
                                    </Box>
                                  </a>
                                </Box>
                              </Grid>
                              {/* Bottom Row */}
                              <Grid
                                item
                                xs={12}
                                sx={{
                                  borderTop: "1px solid #fff2",
                                  mt: 0,
                                  ml: {xs:5, md:50},
                                  pt: 2,
                                  display: "flex",
                                  flexDirection: { xs: "column", md: "row" },
                                  justifyContent: "space-between",
                                  alignItems: "center"
                                }}
                              >
                                <Typography variant="body2" sx={{ fontSize: 16,  fontFamily: "Josefin Sans", }}>
                                  {footers.copyright}
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: 16,  fontFamily: "Josefin Sans", }}>
                                  {footers.made}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>
    </Box>
  );
}

export default PhotoSelection;